import React, { useState } from 'react';
import { productsData } from '../data/productsData';

const ProductsCatalog = () => {
  const [activeCategory, setActiveCategory] = useState('All Spares');

  const categories = [
    'All Spares',
    'Burners & Chucks',
    'Ampoule & Vial Spares',
    'SM-3 Spares',
    'Custom Tooling'
  ];

  const filteredProducts = activeCategory === 'All Spares'
    ? productsData
    : productsData.filter(product => product.category === activeCategory);

  return (
    <section id="products" className="products-catalog-section py-5 bg-light border-bottom">
      <div className="container py-lg-4">
        {/* Section Header */}
        <div className="text-center max-w-700 mx-auto mb-4">
          <div className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill bg-white border text-primary small fw-semibold mb-2 shadow-2xs">
            <i className="fas fa-boxes"></i>
            <span>B2B Spare Parts Catalog</span>
          </div>
          <h2 className="fw-bold text-dark mb-2 display-6">
            Comprehensive <b>Machinery Spares Range</b>
          </h2>
          <div className="text-primary fw-semibold fs-5 mb-2">
            Engineered for Maximum Efficiency &amp; Zero Downtime
          </div>
          <p className="text-muted fs-6 mb-0" style={{ lineHeight: '1.6' }}>
            High-precision replacement spares and assemblies purpose-built for pharmaceutical ampoule and vial manufacturing lines. Custom specifications and drawings accepted.
          </p>
        </div>

        {/* Category Filter Tabs */}
        <div className="d-flex flex-wrap justify-content-center gap-2 mb-5">
          {categories.map((cat, idx) => (
            <button
              key={idx}
              type="button"
              className={`btn btn-sm px-3 py-2 rounded-pill fw-medium transition-all ${
                activeCategory === cat
                  ? 'btn-primary shadow-sm'
                  : 'btn-white bg-white border text-secondary hover-primary'
              }`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Products Grid - 2 columns on mobile (col-6), 3 on desktop (col-lg-4), 4 on wide screens (col-xl-3) */}
        <div className="row g-2 g-sm-3 g-lg-4">
          {filteredProducts.map((product) => (
            <div key={product.id} className="col-6 col-lg-4 col-xl-3">
              <div className="product-card bg-white border rounded-3 overflow-hidden h-100 d-flex flex-column justify-content-between shadow-2xs">
                <div>
                  {/* Standardized Product Image Container - Edge-to-edge Cover Fitting */}
                  <div 
                    className="product-image-container position-relative bg-light border-bottom overflow-hidden"
                  >
                    <img 
                      src={product.image} 
                      alt={product.name} 
                      className="product-image w-100 h-100"
                      style={{ objectFit: 'cover', objectPosition: 'center' }}
                      loading="lazy"
                    />
                    {/* Category Pill on Image (hidden on extra-small mobile to prevent overcrowding) */}
                    <span className="badge bg-dark bg-opacity-75 text-white position-absolute top-0 start-0 m-1 m-md-2 product-badge-cat fw-normal">
                      {product.category}
                    </span>
                    {/* Precision Badge on Image */}
                    <span className="badge bg-primary text-white position-absolute top-0 end-0 m-1 m-md-2 product-badge-tag">
                      {product.badge}
                    </span>
                  </div>

                  {/* Card Content with Responsive Heights & Typography */}
                  <div className="p-2 p-md-3">
                    <h5 
                      className="product-card-title fw-bold text-dark mb-1 mb-md-2 d-flex align-items-center"
                    >
                      {product.name}
                    </h5>

                    {/* Short Description */}
                    <p 
                      className="product-card-desc text-secondary small mb-2 mb-md-3"
                    >
                      {product.description}
                    </p>

                    {/* Technical Specifications Box */}
                    <div 
                      className="specs-box bg-light border rounded-2 p-1 p-md-2 mb-1 mb-md-2 d-flex flex-column justify-content-between"
                    >
                      <div className="specs-title fw-bold text-muted text-uppercase mb-1 border-bottom pb-1">
                        Technical Specifications
                      </div>
                      <div className="d-flex flex-column gap-1">
                        {product.specs.map((spec, sIdx) => (
                          <div key={sIdx} className="specs-row d-flex justify-content-between">
                            <span className="text-muted text-truncate me-1">{spec.label}:</span>
                            <span className="fw-medium text-dark text-end">{spec.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Bottom CTA */}
                <div className="p-2 p-md-3 pt-0 d-flex gap-1 gap-md-2">
                  <a 
                    href="#contact" 
                    className="btn btn-primary btn-sm w-100 fw-semibold d-inline-flex align-items-center justify-content-center gap-1 rounded-2 py-1 px-1 px-md-2"
                  >
                    <i className="fas fa-file-invoice small"></i>
                    <span className="d-none d-sm-inline">Request Specs / RFQ</span>
                    <span className="d-inline d-sm-none" style={{ fontSize: '0.72rem' }}>RFQ</span>
                  </a>
                  <a 
                    href={`https://wa.me/918108765006?text=${encodeURIComponent(`Hello, I am interested in inquiring about ${product.name} from A.B. Engineering Works.`)}`}
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="btn btn-outline-success btn-sm px-2 rounded-2 d-flex align-items-center justify-content-center"
                    title="Inquire via WhatsApp"
                  >
                    <i className="fab fa-whatsapp"></i>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Callout Banner */}
        <div className="mt-5 p-4 bg-white border rounded-3 shadow-sm">
          <div className="row align-items-center g-3">
            <div className="col-lg-8">
              <div className="d-flex align-items-center gap-3">
                <div className="bg-primary text-white rounded-circle d-none d-sm-flex align-items-center justify-content-center p-3 flex-shrink-0" style={{ width: '50px', height: '50px' }}>
                  <i className="fas fa-drafting-compass fs-4"></i>
                </div>
                <div>
                  <h5 className="fw-bold text-dark mb-1">Need a Custom Part or Emergency Breakdown Repair?</h5>
                  <p className="text-muted small mb-0">
                    We fabricate custom replacement tooling directly from sample components or 2D/3D CAD drawings with emergency same-day dispatch support.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-lg-4 text-lg-end text-start">
              <a href="#contact" className="btn btn-primary px-4 py-2 fw-semibold rounded-2 shadow-xs">
                <i className="fas fa-envelope-open-text me-2"></i>Send Custom Specs
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductsCatalog;
